"use client";

import { useSearchParams } from "react-router-dom";
import { useLiveQuery } from "@/core/hooks/useLiveQuery";
import { offlineDb, type OfflineCampagne } from "@/core/offline/db";
import { useCampagneActive } from "./useCampagneActive";

/**
 * Hook "campagne consultée" : résout quelle campagne l'utilisateur est en train
 * de visualiser, en combinant le paramètre d'URL `?campagneId=...` et la
 * campagne active du site.
 *
 * - Si `?campagneId=X` présent dans l'URL ET campagne appartient bien au site :
 *   retourne cette campagne + mode `isViewingHistorical = true`
 * - Sinon : retourne la campagne active (ou undefined) + mode normal
 *
 * L'objectif : permettre de naviguer vers une campagne clôturée et voir
 * ses mesures (read-only), tout en gardant les pages site/zone/pin
 * cohérentes avec la campagne en cours par défaut.
 */
export function useViewedCampagne(siteId: string) {
  const [searchParams] = useSearchParams();
  const viewedCampagneId = searchParams.get("campagneId");

  // Campagne demandée via URL (si fournie)
  const urlCampagne = useLiveQuery(
    async () => {
      if (!viewedCampagneId) return undefined;
      const c = await offlineDb.campagnes.get(viewedCampagneId);
      // Valide que la campagne existe ET appartient bien au site ciblé
      if (!c || c.siteId !== siteId) return undefined;
      return c;
    },
    [viewedCampagneId, siteId]
  );

  // Campagne active + historique complet du site
  const { campagne: activeCampagne, campagnes, campagnesLoading } = useCampagneActive(siteId);

  const isViewingHistorical = !!viewedCampagneId && !!urlCampagne;
  const viewedCampagne: OfflineCampagne | undefined = isViewingHistorical
    ? urlCampagne
    : activeCampagne;

  return {
    viewedCampagne,        // campagne à utiliser pour afficher les mesures
    activeCampagne,        // campagne active du site (utile pour afficher "retour à la campagne actuelle")
    isViewingHistorical,   // vrai si on regarde une campagne autre que l'active
    campagnes,             // historique complet
    campagnesLoading,
  };
}
